/**
 * 課題管理の「やること」・Backlog 案件・滞留通知の読み取り（oRPC `issueTodo` / `issueMgmt`）。
 *
 * 書き込みは台帳の書き込み（`useIssueMgmtWrite`）を使い、読み直しはそちらが決める。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理のやることのカード・AI が除外した投稿・課題の案件欄・Backlog 同期の状況・紐づけの候補・滞留通知の状況を読む
 * @graph-connects rpcClient [calls] issueTodo・issueMgmt の手続き
 */
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { rpcClient } from '../lib/orpc.js';
import { queryKeys } from '../lib/query-keys.js';

/**
 * やることのカード（並び順どおり）と週次の案内。enabled が false なら読まない（閲覧キーだけの人）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business やることのカードの一覧と週次の案内を取得する
 * @graph-connects rpcClient [calls] issueTodo.cards
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useTodoCards(enabled = true) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.todoCards,
    queryFn: () => rpcClient.issueTodo.cards(),
    enabled,
  });
}

/**
 * AI が除外した直近30日の投稿（新しい順に先頭 limit 件と全件数）。開いたときだけ読む。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business AI が除外した直近30日の投稿を取得する
 * @graph-connects rpcClient [calls] issueTodo.excluded
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useExcludedVoices(limit: number, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.excluded(limit),
    queryFn: () => rpcClient.issueTodo.excluded({ limit }),
    placeholderData: keepPreviousData,
    enabled,
  });
}

/**
 * 課題1件の案件欄（Backlog 案件の写し・進捗・確かめてもらう理由）。enabled が false なら読まない。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題に紐づく Backlog 案件の写しと進捗・完了と未完了の件数・確かめてもらう理由を取得する
 * @graph-connects rpcClient [calls] issueMgmt.cases
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useIssueCases(issueId: string, enabled = true) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.cases(issueId),
    queryFn: () => rpcClient.issueMgmt.cases({ issueId }),
    enabled,
  });
}

/**
 * Backlog 同期の状況（ミラーの鮮度・実行中か・最後の実行・最終同期）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Backlog 同期の状況（ミラーが古いかの警告・最後の実行・最終同期）を取得する
 * @graph-connects rpcClient [calls] issueMgmt.backlogSync
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useBacklogSync() {
  return useQuery({
    queryKey: queryKeys.issueMgmt.backlogSync,
    queryFn: () => rpcClient.issueMgmt.backlogSync(),
  });
}

/**
 * 案件を紐づける小窓の候補（未対応のメンズ案件・作成日の新しい順）。小窓を開いたときだけ読む。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 案件を紐づける小窓に出す、Backlog の未対応のメンズ案件を取得する
 * @graph-connects rpcClient [calls] issueMgmt.caseCandidates
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useCaseCandidates(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.issueMgmt.caseCandidates,
    queryFn: () => rpcClient.issueMgmt.caseCandidates(),
    enabled,
    staleTime: 60_000,
  });
}

/**
 * 滞留通知の状況（最後の実行と結果・前回送れなかった時刻）。
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 課題管理の滞留通知の最後の実行と、送れなかった時刻を取得する
 * @graph-connects rpcClient [calls] issueMgmt.staleNotify
 * @graph-connects queryKeys [calls] Query key factory
 */
export function useStaleNotify() {
  return useQuery({
    queryKey: queryKeys.issueMgmt.staleNotify,
    queryFn: () => rpcClient.issueMgmt.staleNotify(),
  });
}
